import React from 'react';
import { format, parseISO } from 'date-fns';
import { Trash2, Heart } from 'lucide-react';
import { BPReading } from '../utils/api';

interface RecentReadingsProps {
  readings: BPReading[];
  onDeleteReading?: (id: number) => void;
  loading?: boolean;
}

const RecentReadings: React.FC<RecentReadingsProps> = ({ 
  readings, 
  onDeleteReading, 
  loading = false 
}) => {
  if (loading) {
    return (
      <div className="card">
        <h3 className="section-header">Recent readings</h3>
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse">
              <div className="flex justify-between items-center py-3">
                <div className="flex items-center space-x-4">
                  <div className="h-8 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="text-right">
                  <div className="h-6 bg-gray-200 rounded w-16 mb-1"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (readings.length === 0) {
    return (
      <div className="card">
        <h3 className="section-header">Recent readings</h3>
        <div className="text-center py-8 text-gray-500">
          <Heart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium mb-2">No readings yet</p>
          <p className="text-sm">Start tracking your blood pressure by adding your first reading</p>
        </div>
      </div>
    );
  }

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return 'Just now';
    try {
      const date = parseISO(timestamp);
      return format(date, 'MMM d, h:mm a');
    } catch {
      return 'Invalid date';
    }
  };

  const getBPStatus = (systolic: number, diastolic: number) => {
    if (systolic >= 140 || diastolic >= 90) {
      return { status: 'High', color: 'text-red-600 bg-red-50' };
    } else if (systolic >= 130 || diastolic >= 80) {
      return { status: 'Elevated', color: 'text-yellow-600 bg-yellow-50' };
    } else {
      return { status: 'Normal', color: 'text-green-600 bg-green-50' };
    }
  };

  return (
    <div className="card">
      <h3 className="section-header">Recent readings</h3>
      
      <div className="space-y-3">
        {readings.slice(0, 5).map((reading) => {
          const bpStatus = getBPStatus(reading.systolic, reading.diastolic);
          
          return (
            <div 
              key={reading.id} 
              className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 rounded-lg px-2 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className="bp-reading">
                  {reading.systolic}/{reading.diastolic}
                  <span className="text-base font-normal text-gray-600 ml-1">mmHg</span>
                </div>
                
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${bpStatus.color}`}>
                  {bpStatus.status}
                </div>
                
                {reading.notes && (
                  <div className="text-sm text-gray-500 italic max-w-xs truncate">
                    "{reading.notes}"
                  </div>
                )}
              </div>
              
              <div className="text-right flex items-center space-x-3">
                <div>
                  {reading.heart_rate && (
                    <div className="bp-metadata font-semibold text-gray-700">
                      {reading.heart_rate} bpm
                    </div>
                  )}
                  <div className="bp-metadata">
                    {formatTimestamp(reading.timestamp)}
                  </div>
                </div>
                
                {onDeleteReading && reading.id && (
                  <button
                    onClick={() => onDeleteReading(reading.id!)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded"
                    title="Delete reading"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {readings.length > 5 && (
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500">
            Showing 5 of {readings.length} readings
          </p>
        </div>
      )}
    </div>
  );
};

export default RecentReadings;